import { Box, Flex, UseDisclosureReturn } from '@chakra-ui/react';
import { EntityNoteModel } from '../../globalModels/entityNotes/entityNoteModel';
import { BasePopover, FormContainer, createFieldHelper, useHookForm, useStandardToast } from '@cks-internal/react-base';
import { useEntityNoteMutation } from './apis/useEntityNoteMutation';
import { EntityNoteQueryType } from '../../enums/common/EntityNoteQueryType';
import { PropsWithChildren } from 'react';
import dayjs from 'dayjs';
import { formatTimeForBackend } from '../activities/editActivity/toolbar/followUpModal/utils/followUpFormatters';

type PinNotePopoverProps = PropsWithChildren & {
  disclosure: UseDisclosureReturn;
  model: EntityNoteModel;
  id?: number;
  queryType: EntityNoteQueryType;
  onPinned: () => void;
};

type PinNoteFormModel = {
  expiryDate?: string;
  expiryTime?: string;
};

export const PinNotePopover = (props: PinNotePopoverProps) => {
  const toast = useStandardToast();
  const { formMethods } = useHookForm<PinNoteFormModel>({
    expiryDate: undefined,
    expiryTime: undefined
  });
  const helper = createFieldHelper<PinNoteFormModel>(formMethods);
  const updateNoteMutation = useEntityNoteMutation();

  const isValidTimeInput = (time?: string) => {
    if (!time) return true;
    return dayjs(`2000-01-01 ${time}`).isValid();
  };

  const fields = [
    helper
      .dateInput('expiryDate')
      .withLabel('Expires On Date')
      .withRenderIf(() => !props.model.IsPinned)
      .withValidation({
        validate: (date, formValues) => {
          const time = formValues.expiryTime;
          if (time && !date) {
            return 'Expiry date is required when a time is provided';
          }
          return undefined;
        }
      }),
    helper
      .timeInput('expiryTime')
      .withLabel('Expires On Time')
      .withRenderIf(() => !props.model.IsPinned)
      .withValidation({
        validate: (time) => {
          if (!time) return undefined;
          if (!isValidTimeInput(time)) return 'Please enter a valid time';
          return undefined;
        }
      })
  ];

  const onSubmit = async (formModel: PinNoteFormModel) => {
    const isPinning = !props.model.IsPinned;

    const backendTime = formModel.expiryTime ? formatTimeForBackend(formModel.expiryTime) : null;
    const newExpiresOnDate = isPinning ? formModel.expiryDate || undefined : undefined;
    const newExpiresOnTime = isPinning ? backendTime : null;
    const newTimeZoneId = props.model.ExpiresOnTimeZoneId || Intl.DateTimeFormat().resolvedOptions().timeZone;

    const updatedNote: EntityNoteModel = {
      ...props.model,
      IsPinned: isPinning,
      ExpiresOnDate: newExpiresOnDate,
      ExpiresOnTime: newExpiresOnTime,
      ExpiresOnTimeZoneId: newTimeZoneId
    };

    try {
      await updateNoteMutation.mutateAsync({ noteType: props.queryType, id: props.id, model: updatedNote });

      // Only update local model after successful save to avoid FE/BE drift
      props.model.IsPinned = isPinning;
      props.model.ExpiresOnDate = newExpiresOnDate;
      props.model.ExpiresOnTime = newExpiresOnTime;
      props.model.ExpiresOnTimeZoneId = newTimeZoneId;

      props.disclosure.onClose();
      formMethods.reset();
      props.onPinned();
      toast.successToast('Success', isPinning ? 'Note pinned' : 'Note unpinned');
    } catch {
      // Errors are handled by global axios/react-query handlers
    }
  };

  const handleClose = () => {
    formMethods.reset();
    props.disclosure.onClose();
  };

  const handleOk = async () => {
    const isValid = await formMethods.trigger();
    if (!isValid) return;
    await onSubmit(formMethods.getValues());
  };

  return (
    <BasePopover
      trigger={props.children}
      contentProps={{ width: '420px' }}
      disclosure={props.disclosure}
      isOkayDisabled={updateNoteMutation.isPending || !formMethods.formState.isValid}
      onClose={handleClose}
      onCancel={handleClose}
      onOk={handleOk}
      okayButtonText={props.model.IsPinned ? 'Unpin' : 'Pin'}>
      <FormContainer<PinNoteFormModel> methods={formMethods} hideControls hideDebugOnLocal>
        <Flex gap={4} flexDir={'column'} w={'100%'}>
          <Box fontSize='sm' color='gray.600'>
            {!props.model.IsPinned ? (
              <>
                <Box mb={2}>
                  Pin this note to <b>enable</b> it to be displayed to agents.
                </Box>
                <Box mb={2}>
                  Optionally set an <b>expiry date and time</b> to automatically unpin the note.
                </Box>
                <Box color='gray.600'>
                  Expires at the <b>selected date and time</b>.
                </Box>
              </>
            ) : (
              <>
                <Box mb={2}>
                  Unpin this note to <b>hide</b> it from agents.
                </Box>
                <Box color='gray.600'>
                  You can optionally set an <b>expiry date and time</b> from the note editor.
                </Box>
              </>
            )}
          </Box>
          {!props.model.IsPinned && (
            <Flex gap={4} flexDir={'column'} w='100%'>
              {helper.renderFieldHelperInput('expiryDate', fields)}
              {helper.renderFieldHelperInput('expiryTime', fields)}
            </Flex>
          )}
        </Flex>
      </FormContainer>
    </BasePopover>
  );
};
